import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { NotImplementedError } from "../../../../../gyzmo-commons/helpers/notImplementedError";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { DamageValue } from "../../../../models/inspection/common/damageValue.model";

@Injectable({
    providedIn: "root",
})
export class DamageValuesDbDao extends DbDaoBase<DamageValue> {
    constructor(logger: LoggerService,
                private sqlProvider: AppSqlProvider) {
        super(logger);
    }

    public getList(): Promise<DamageValue[]> {
        const selectQuery = `SELECT * FROM ${ DamageValue.TABLENAME };`;
        return this.startListQuery(selectQuery, []);
    }

    public getByCategoryId(categoryId: string): Promise<DamageValue[]> {
        const selectQuery = `SELECT * FROM ${ DamageValue.TABLENAME } WHERE categoryId = ?;`;
        return this.startListQuery(selectQuery, [categoryId]);
    }

    public async createIndexes(): Promise<void> {
        const query = `CREATE INDEX IF NOT EXISTS idx_${ DamageValue.TABLENAME }_categoryId
                       ON ${ DamageValue.TABLENAME }(categoryId);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ DamageValue.TABLENAME } (
            id TEXT,
            categoryId TEXT,
            wording TEXT,
            _order TEXT,
            key TEXT PRIMARY KEY
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ DamageValue.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    public deleteAll(): Promise<any> {
        const deleteQuery = `DELETE FROM ${ DamageValue.TABLENAME };`;
        return this.sqlProvider.query(deleteQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<DamageValue> {
        throw new NotImplementedError();
    }

    public getTableName(): string {
        return DamageValue.TABLENAME;
    }

    protected rowToModel(row: any): DamageValue {
        const damageValue = new DamageValue();
        damageValue.id = row.id;
        damageValue.categoryId = row.categoryId;
        damageValue.wording = row.wording;
        damageValue.order = row._order;
        return damageValue;
    }

    public save(damageValue: DamageValue): Promise<DamageValue> {
        const query = `INSERT OR REPLACE INTO ${ DamageValue.TABLENAME } (
            id, categoryId, wording, _order, key
        ) VALUES (?, ?, ?, ?, ?);`;
        const params = [
            damageValue.id,
            damageValue.categoryId,
            damageValue.wording,
            damageValue.order,
            `${ damageValue.id }_${ damageValue.categoryId }`,
        ];

        return this.sqlProvider.query(query, params)
            .then(() => damageValue)
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    private startListQuery(query: string, params: any[]): Promise<DamageValue[]> {
        return this.sqlProvider.query(query, params)
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const damageValues: DamageValue[] = [];
                for (const item of data.rows) {
                    const damageValue = this.rowToModel(item);
                    damageValues.push(damageValue);
                }

                return damageValues;
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }
}
